<template>
  <div v-if="!isReadonly" class="asset-drawer__form">
    <UIInputDynamicList
      v-model="tagName"
      data-refid="assetTagsFormName"
      :data="tagsNames"
      :disabled="isLoading"
      placeholder="Tag name"
      size="sm"
      class="asset-drawer__form-input"
      @click:new="handleTagNameCreate"
      @click:item="handleTagNameSelect"
    />
    <AssetDrawerTagsValues
      ref="tagValueRef"
      v-model="tagValue"
      :tag-name
      :asset-id
      :loading="isLoading"
      data-refid="assetTagsFormValue"
      size="sm"
      class="asset-drawer__form-input"
      @value:assign="handleAssign"
      @value:select="handleValueSelect"
    />
    <UIButton
      ref="buttonRef"
      data-refid="assetTagsFormAdd"
      :disabled="isButtonDisabled"
      :leading="PlusIcon"
      label="Tag"
      variant="secondary"
      size="sm"
      hide-label-to-sm
      @click="handleClickPlus"
    />
  </div>
  <UIGridSkeleton
    v-if="isLoading"
    :columns
    :sm
    message="Loading tags..."
  />
  <UIGrid
    v-else
    v-model:sort="sort"
    data-refid="assetTagsList"
    :columns
    :items
    :sm
    :edit-row-id
    editable-rows
  >
    <template #cellTagValue="{ editRow, item, size }">
      <div v-if="editRow" class="asset-drawer-tags__edit-row" @mouseup.stop @mousedown.stop>
        <AssetDrawerTagsValues
          :model-value="item.tag_value"
          :tag-name="item.tag_name"
          :asset-id
          :loading="isLoading"
          :size
          focus-on-load
          @value:assign="(tag, callback) => handleReAssignNew(item.id, tag, callback)"
          @value:select="handleReAssign(item.id, $event)"
        />
        <UIButton
          title="Cancel"
          :leading="XMarkIcon"
          :disabled="isLoading"
          type="button"
          variant="danger"
          fill="light"
          :size
          @click="handleCancelEdit"
        />
      </div>
      <template v-else>{{ item.tag_value }}</template>
    </template>
    <template v-if="!isReadonly" #actions="{ item, size }">
      <template v-if="item">
        <UIButton
          label="Edit"
          :leading="PencilIcon"
          variant="secondary"
          fill="light"
          :size
          @click="handleClickEdit(item)"
        />
        <UIButton
          label="Delete"
          :leading="TrashIcon"
          variant="danger"
          fill="light"
          :size
          @click="handleClickDelete(item)"
        />
      </template>
    </template>
  </UIGrid>
</template>

<script setup lang="ts">
import { computed, inject, markRaw, nextTick, ref, useTemplateRef } from 'vue'

import { AssetClass } from '..'
import { TagClass } from '@/entities/tags'
import { ReadonlyMode, Sort, Tag } from '@types'

import { ASSET_FIELD } from '../utils/const'
import { TAG_FIELD } from '@/entities/tags/utils/const'
import { READONLY_MODE } from '@/const/common'

import { handleCatchedError } from '@/helpers/common'
import { getOriginalValues } from './utils/helpers'

import { useNotifications } from '@/plugins/notification'

import useGridSort from '@/components/hooks/gridSort'

import useAnalyticsStore from '@/store/analytics'
import { useModalsStore } from '@/store/modals'
import { useTagsBunchStore } from '@/store/tags/bunch'
import { useTagsStore } from '@/store/tags'

import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/vue/24/outline'
import { UIButton } from '@ui/buttons'
import { UIGrid, UIGridSkeleton } from '@ui/grid'
import { UIDialogRemove } from '@ui/modals'
import { UIInputDynamicList } from '@ui/fields'
import AssetDrawerTagsValues from './AssetDrawerTagsValues.vue'
import { useHotKeys } from '@/plugins/hotKeys'
import { XMarkIcon } from '@heroicons/vue/20/solid'

type Props = {
  instance: AssetClass
}

const props = defineProps<Props>()

defineOptions({ inheritAttrs: false })

const analyticsStore = useAnalyticsStore()
const modalsStore = useModalsStore()
const tagsBunchStore = useTagsBunchStore()
const tagsStore = useTagsStore()

const { error } = useNotifications()

const editRowId = ref<string>()

const tagName = ref<string>()
const tagValue = ref<string>()

const tagValueRef = useTemplateRef('tagValueRef')
const buttonRef = useTemplateRef('buttonRef')

const loading = ref(false)

const sort = ref<Sort[]>()

const removedTag = ref<Tag>()
const internalTagNames = ref<string[]>([])

const isReadonly = inject<ReadonlyMode>(READONLY_MODE)

const tagsField = props.instance.field<string[]>(ASSET_FIELD.TAGS)

const isLoading = computed(
  () => loading.value || !tagsStore.initFlag,
)

const isButtonDisabled = computed(
  () => isLoading.value || !tagName.value || !tagValue.value,
)

const assetId = computed(() => props.instance.id)
const tagsList = computed(() => Array.from(tagsBunchStore.getCommonList))

const sm = '1fr'
const columns = computed(() => [
  {
    name: 'tag_name',
    caption: 'Tag name',
    hideCaptionIfCollapsed: true,
  },
  {
    name: 'tag_value',
    caption: 'Tag value',
    hideCaptionIfCollapsed: true,
  },
])

const inputItems = computed(() => {
  const result = []
  for (let i = 0; i < tagsField.value.length; i++) {
    const tagId = tagsField.value[i]
    const tagInstance = tagsBunchStore.getList.get(tagId)
    if (!tagInstance) continue
    result.push(tagInstance.get())
  }
  return result
})
const items = useGridSort(sort, columns, inputItems, undefined, ref('tag_name'), ref('asc'))

const tagsNames = computed(() => {
  const mappedData = tagsList.value.map(
    ([, instance]) => instance.field<string>(TAG_FIELD.TAG_NAME).value,
  )
  return getOriginalValues([...internalTagNames.value, ...mappedData])
})

const handleTagNameCreate = async (name: string, callback: () => void) => {
  tagName.value = name
  internalTagNames.value.push(name)
  callback()
  setTimeout(() => tagValueRef.value?.focus(), 0)
}

const handleTagNameSelect = async () => {
  await nextTick()
  tagValueRef.value?.focus()
}

const handleClickEdit = (item: Tag) => {
  editRowId.value = item.id
}

const handleClickDelete = (item: Tag) => {
  removedTag.value = item
  const key = `asset-remove-tag-${item.id}`
  const modalInstance = modalsStore.init(key, markRaw(UIDialogRemove))
  modalInstance?.open(modalsStore.getZIndex(), {
    title: `Remove tag '${removedTag.value?.tag_name}: ${removedTag.value?.tag_value}'`,
    message: 'Are you sure you want to remove this tag?',
    label: 'Delete',
    onRemove: handleDelete
  })
}

const handleDelete = async (id?: string, update = true) => {
  const tagId = removedTag.value?.id || id
  removedTag.value = undefined
  if (!tagId) return
  loading.value = true
  try {
    tagsField.value = items.value
      .map(item => item.id)
      .filter(item => item !== tagId)
    update && await handleUpdate()
  } catch (e) {
    await error({
      message: 'There was a problem with the tag',
    })
    handleCatchedError(e as string, props.instance.get())
  } finally {
    loading.value = false
  }
}

const handleClickPlus = async () => {
  loading.value = true
  try {
    const tagInstance = tagsBunchStore.getElementByPair(
      tagName.value,
      tagValue.value,
    )
    if (tagInstance) {
      await handleAssign(tagInstance)
    } else {
      await error({
        message: 'There is no such tag',
      })
    }
  } catch (e) {
    await error({
      message: 'There was a problem with the tag',
    })
    handleCatchedError(e as string)
  } finally {
    loading.value = false
  }
}

const handleValueSelect = () => {
  buttonRef.value?.focus()
}

const handleAssign = async (tag: TagClass, callback?: () => void) => {
  const assetTag = items.value.find(
    item => item.tag_name === tag.field(TAG_FIELD.TAG_NAME).value,
  )
  if (assetTag) {
    callback?.()
    loading.value = false
    await error({
      message: 'A tag with this name already exists',
    })
    return
  }
  tagName.value = undefined
  tagValue.value = undefined
  tagsField.value.push(tag.id)
  await handleUpdate()
  callback?.()
}

const handleReAssign = async (id: string, tag: TagClass) => {
  loading.value = true
  await handleDelete(id, false)
  await handleAssign(tag)
  loading.value = false
  editRowId.value = undefined
}

const handleReAssignNew = async (id: string, tag: TagClass, callback?: () => void) => {
  await handleReAssign(id, tag)
  callback?.()
}

const handleUpdate = async () => {
  await props.instance.update()
  analyticsStore.markAsWaitingToRefresh()
}

const handleCancelEdit = () => {
  editRowId.value = undefined
}

useHotKeys(
  'escape',
  e => {
    e?.stopPropagation()
    if (!editRowId.value) return
    editRowId.value = undefined
    return true
  },
  { priority: 90 },
)
</script>

<style>
.asset-drawer-tags__edit-row {
  @apply w-full;
  @apply flex gap-2;

  .v-popper {
    @apply flex-auto;
  }
}
</style>
